import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ListingCard from '../components/ListingCard';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, X, MapPin, ArrowRight } from 'lucide-react';
import { OPPORTUNITY_TYPES, INDUSTRIES } from '../constants';

const BrowsePage = () => {
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [opportunityType, setOpportunityType] = useState(searchParams.get('opportunity_type') || '');
  const [industry, setIndustry] = useState('');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [sort, setSort] = useState('newest');
  const [cities, setCities] = useState([]);
  const [cityInput, setCityInput] = useState(searchParams.get('city') || '');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // committedSearch updates after user stops typing (debounced) OR on submit
  const [committedSearch, setCommittedSearch] = useState('');

  // Debounce live search
  useEffect(() => {
    const t = setTimeout(() => setCommittedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchCities = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/listings/cities`);
      setCities(data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  }, [API_URL]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (opportunityType) params.append('opportunity_type', opportunityType);
      if (industry) params.append('industry', industry);
      if (city) params.append('city', city);
      if (committedSearch) params.append('search', committedSearch);
      params.append('sort', sort);

      const { data } = await axios.get(`${API_URL}/api/listings?${params.toString()}`);
      setListings(data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, opportunityType, industry, city, sort, committedSearch]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCommittedSearch(search);
  };

  const handleCitySelect = (selectedCity) => {
    setCity(selectedCity);
    setCityInput(selectedCity);
    setShowCitySuggestions(false);
  };

  const handleCityInputChange = (value) => {
    setCityInput(value);
    setShowCitySuggestions(value.length > 0);
    if (!value) setCity('');
  };

  const filteredCities = cities.filter((c) => c.toLowerCase().includes(cityInput.toLowerCase()));
  const hasActiveFilters = opportunityType || industry || search || city;

  const clearFilters = () => {
    setOpportunityType('');
    setIndustry('');
    setSearch('');
    setCommittedSearch('');
    setCity('');
    setCityInput('');
    setSort('newest');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />

      <SEO
        title="Browse Jobs, Gigs, Residencies & Grants"
        path="/browse"
        description="Browse curated creative jobs, gigs, internships, residencies, grants, fellowships and open calls across India's arts, culture and design sector."
      />

      {/* Header */}
      <section className="relative px-6 md:px-12 lg:px-24 py-20 md:py-24 overflow-hidden" style={{ backgroundColor: '#0F2942' }}>
        {/* Warm photo backdrop on right */}
        <div className="hidden md:block absolute right-0 top-0 bottom-0 w-1/2 opacity-30" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1719398026703-0d3f3d162e51?w=1000&q=60&auto=format&fit=crop"
            alt=""
            loading="lazy"
            decoding="async"
            width="1000"
            height="600"
            className="w-full h-full object-cover"
            style={{ maskImage: 'linear-gradient(to left, black, transparent)', WebkitMaskImage: 'linear-gradient(to left, black, transparent)' }}
          />
        </div>
        <div className="relative max-w-3xl">
          <h1
            className="text-4xl md:text-5xl font-light tracking-tight leading-none mb-3 text-white"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Browse Opportunities
          </h1>
          <div className="w-16 h-px bg-white/30 mb-4" />
          <p className="text-base font-light" style={{ color: 'rgba(240, 235, 226, 0.6)' }}>
            Search and filter across all active opportunities.
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="px-6 md:px-12 lg:px-24 py-10" style={{ backgroundColor: '#F0EBE2' }}>
        <form onSubmit={handleSearch} className="space-y-6">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-6 h-6" style={{ color: '#0F2942' }} strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Search by role, organisation, city, state, remote, or keyword..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-2xl md:text-3xl border-0 border-b-2 rounded-none bg-transparent pl-10 pb-3 focus-visible:ring-0 focus-visible:ring-offset-0"
              style={{ fontFamily: 'Cormorant Garamond, serif', borderColor: '#0F2942', color: '#0F2942' }}
              data-testid="search-input"
            />
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium mb-3" style={{ color: '#0F2942' }}>Opportunity Type</p>
            <div className="flex flex-wrap gap-2" data-testid="opportunity-type-filter">
              {OPPORTUNITY_TYPES.map((ot) => (
                <button
                  key={ot}
                  type="button"
                  onClick={() => setOpportunityType(opportunityType === ot ? '' : ot)}
                  className="px-4 py-2 text-sm border rounded-lg transition-colors"
                  style={opportunityType === ot
                    ? { backgroundColor: '#0F2942', color: '#FFFFFF', borderColor: '#0F2942' }
                    : { backgroundColor: '#FFFFFF', color: '#0F2942', borderColor: '#F0EBE2' }
                  }
                  data-testid={`opp-type-${ot.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {ot}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select value={industry} onValueChange={(val) => setIndustry(val === 'all' ? '' : val)}>
              <SelectTrigger className="rounded-lg bg-white" data-testid="filter-industry">
                <SelectValue placeholder="Industry / Domain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {INDUSTRIES.map((ind) => (
                  <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#0F2942' }} strokeWidth={1.5} />
                <Input
                  type="text"
                  placeholder="City / Location"
                  value={cityInput}
                  onChange={(e) => handleCityInputChange(e.target.value)}
                  onFocus={() => cityInput && setShowCitySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                  className="rounded-lg pl-9 h-10 bg-white"
                  data-testid="city-filter-input"
                />
              </div>
              {showCitySuggestions && filteredCities.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg max-h-48 overflow-y-auto shadow-lg" style={{ borderColor: '#F0EBE2' }}>
                  {filteredCities.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onMouseDown={() => handleCitySelect(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EBE2] transition-colors flex items-center gap-2"
                    >
                      <MapPin className="w-3 h-3" style={{ color: '#0F2942' }} strokeWidth={1.5} />
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="rounded-lg bg-white" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="closing_soon">Closing Soon</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                onClick={clearFilters}
                className="rounded-lg border bg-white text-sm"
                style={{ borderColor: '#F0EBE2', color: '#0F2942' }}
                data-testid="clear-filters-button"
              >
                <X className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Clear Filters
              </Button>
            )}
          </div>
        </form>
      </section>

      {/* Results */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        {loading ? (
          <div className="text-center py-20" data-testid="browse-loading">
            <div
              className="inline-block w-6 h-6 rounded-full mb-4 animate-spin"
              style={{
                border: '2px solid rgba(15,41,66,0.15)',
                borderTopColor: '#0F2942'
              }}
              aria-hidden="true"
            />
            <p style={{ color: '#0F2942' }}>
              {committedSearch ? `Searching for "${committedSearch}"…` : 'Loading opportunities…'}
            </p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <h3 className="text-2xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
              No opportunities found
            </h3>
            <p className="text-base mb-2" style={{ color: '#0F2942' }}>
              {committedSearch
                ? <>We couldn&rsquo;t find anything for <strong style={{ color: '#0F2942' }}>&ldquo;{committedSearch}&rdquo;</strong>.</>
                : 'Try adjusting your filters or search terms.'}
            </p>
            <p className="text-sm mb-8" style={{ color: '#0F2942' }}>
              Try a different keyword, a city or state name, or broaden your filters.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {hasActiveFilters && (
                <Button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-lg px-6 py-3 text-sm font-medium"
                  style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}
                  data-testid="empty-state-clear-cta"
                >
                  Clear all filters
                </Button>
              )}
              <Link
                to="/talent"
                className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all border hover:bg-[#F0EBE2]"
                style={{ borderColor: '#F0EBE2', color: '#0F2942' }}
                data-testid="empty-state-talent-cta"
              >
                Join the Talent Network <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Link>
              <Link
                to="/post-job"
                className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all border hover:bg-[#F0EBE2]"
                style={{ borderColor: '#F0EBE2', color: '#0F2942' }}
                data-testid="empty-state-post-cta"
              >
                Post a Job
              </Link>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm mb-8" style={{ color: '#0F2942' }}>{listings.length} {listings.length === 1 ? 'opportunity' : 'opportunities'} found</p>
            <div className="grid grid-cols-1 gap-6" data-testid="listings-container">
              {listings.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>

            {/* Dual CTA strip below listings */}
            <div
              className="mt-16 rounded-xl px-6 md:px-10 py-8 md:py-10 grid grid-cols-1 md:grid-cols-2 gap-6"
              style={{ backgroundColor: '#0F2942' }}
              data-testid="browse-bottom-cta"
            >
              <div className="md:border-r md:pr-8" style={{ borderColor: 'rgba(212,130,10,0.25)' }}>
                <p className="text-xs uppercase tracking-[0.2em] font-medium mb-2" style={{ color: '#D4820A' }}>
                  For Employers
                </p>
                <h3
                  className="text-xl md:text-2xl font-normal tracking-tight mb-3 text-white"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Hiring for a creative role?
                </h3>
                <Link
                  to="/post-job"
                  className="inline-flex items-center text-sm font-medium"
                  style={{ color: '#D4820A' }}
                  data-testid="browse-post-cta"
                >
                  Post a Job <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.5} />
                </Link>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium mb-2" style={{ color: '#D4820A' }}>
                  For Creatives
                </p>
                <h3
                  className="text-xl md:text-2xl font-normal tracking-tight mb-3 text-white"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Don't see the right fit yet?
                </h3>
                <Link
                  to="/talent"
                  className="inline-flex items-center text-sm font-medium"
                  style={{ color: '#D4820A' }}
                  data-testid="browse-talent-cta"
                >
                  Join Our Talent Network <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default BrowsePage;
